import {
    IAffiliate,
    IBrand,
    ICountry,
    IDecisionMessage,
    IGlobalSettings,
    IMedia,
    IMessage,
    IRequest,
    IRequestStatus,
    ITypeOfAssets,
    ITypeOfCampaign,
    RequestStatus,
    ITheme,
} from "@/models";
import { Status } from "@/models/Status";
import { GetterTree } from "vuex";
import { IRequestState } from "./state";

const getters: GetterTree<IRequestState, any> = {
    getFilteredRequests: state => (brandId: number, status: Status, type: boolean, titleSearch: string): IRequest[] => {

        return state.userRequests?.requests.filter(userRequest =>
            (!brandId || userRequest.brand.brandId === brandId) &&
            (!status || userRequest.requestStatus.name === status) &&
            (!type || userRequest.advice === type) &&
            (!titleSearch || userRequest.campaignName?.toUpperCase().indexOf(titleSearch.toUpperCase()) >= 0),
        );
    },

    getFilteredBrands: state => (brandSearch: string): IBrand[] => {
        if (!!state.brands) {
            return state.brands.map(x => ({ ...x }))
                .sort((a, b) => {
                    return a.name?.localeCompare(b.name);
                })
                .filter(b => !brandSearch || b.name?.toUpperCase().indexOf(brandSearch.toUpperCase()) >= 0);
        } else {
            return [];
        }
    },

    getFilteredCountries: state => (countrySearch: string): ICountry[] => {
        if (!!state.countries) {
            return state.countries.map(x => ({ ...x }))
                .sort((a, b) => {
                    return a.name?.localeCompare(b.name);
                })
                .filter(b => !countrySearch
                    || b.name?.toUpperCase().indexOf(countrySearch.toUpperCase()) >= 0
                    || b.value?.toUpperCase().indexOf(countrySearch.toUpperCase()) >= 0);
        } else {
            return [];
        }
    },

    getFilteredAffiliates: state => (affiliateSearch: string): IAffiliate[] => {
        if (!!state.affiliates) {
            return state.affiliates.map(x => ({ ...x }))
                .sort((a, b) => {
                    return a.name?.localeCompare(b.name);
                })
                .filter(a => !affiliateSearch ||
                    a.name?.toUpperCase().indexOf(affiliateSearch.toUpperCase()) >= 0);
        } else {
            return [];
        }
    },

    getRequestMessages: state => (requestId: number): IMessage[] => {
        return Array.isArray(state.messages) && state.messages?.filter(m => m.requestId === requestId);
    },

    getBrands(state): IBrand[] {
        return state.brands?.map(x => ({ ...x }))
            .sort((a, b) => {
                return a.name?.localeCompare(b.name);
            }) ?? [];
    },

    getAffiliates(state): IAffiliate[] {
        return state.affiliates?.map(x => ({ ...x }))
            .sort((a, b) => {
                return a.name?.localeCompare(b.name);
            }) ?? [];
    },

    getGlobalSettings(state): IGlobalSettings {
        if (!!state.globalSettings) {
            return state.globalSettings;
        } else {
            return {
                globalMessage: "",
            };
        }
    },

    getTypesOfCampaign(state): ITypeOfCampaign[] {
        return state.typesOfCampaign?.map(x => ({ ...x })) ?? [];
    },

    getTypesOfAssets(state): ITypeOfAssets[] {
        return state.typesOfAssets?.map(x => ({ ...x }))
            .sort((a, b) => {
                return a.name?.localeCompare(b.name);
            }) ?? [];
    },

    getMedia(state): IMedia[] {
        return state.media?.map(x => ({ ...x }))
            .sort((a, b) => {
                if (a.name === "OTHERS") {
                    return 1;
                } else if (b.name === "OTHERS") {
                    return -1;
                } else {
                    return a.name.localeCompare(b.name);
                }
            }) ?? [];
    },

    getRequestStatus(state): IRequestStatus[] {
        return state.requestStatus?.map(x => ({ ...x }))
            .sort((a, b) => {
                return a.step - b.step;
            }) ?? [];
    },

    getCountries(state): ICountry[] {
        return state.countries?.map(x => ({ ...x }))
            .sort((a, b) => {
                if (a.name === "GBL") {
                    return -1;
                } else if (b.name === "GBL") {
                    return 1;
                } else {
                    return a.value.localeCompare(b.value);
                }
            }) ?? [];
    },

    getDecisionMessages: state => (): IDecisionMessage[] => {
        return state.decisionMessages ?? [];
    },

    getThemes(state): ITheme[] {
        return state.themes?.map(x => ({ ...x }))
        .sort((a, b) => {
            return a.name?.localeCompare(b.name);
        }) ?? [];
    },
    // Statistics
    getNumberOfFormalComplianceRequests(state): number {
        // tslint:disable-next-line: max-line-length
        return state.statisticsRequest?.requests?.filter(req => !req.advice && req.requestStatus.step === 2 && req.requestStatus.name !== RequestStatus.canceled ).length;
    },

    getNumberOfAdviceRequests(state): number {
        // tslint:disable-next-line: max-line-length
        return state.statisticsRequest?.requests?.filter(req => req.advice && req.requestStatus.step === 2).length;
    },

    getNumberOfValidatedRequests(state): number {
        return state.statisticsRequest?.requests?.filter(req =>
            req.requestStatus.name === RequestStatus.validated).length;
    },

    getNumberOfRejectedRequests(state): number {
        return state.statisticsRequest?.requests?.filter(req =>
            req.requestStatus.name === RequestStatus.rejected).length;
    },

    getNumberOfMixedRequests(state): number {
        return state.statisticsRequest?.requests?.filter(req =>
            req.requestStatus.name === RequestStatus.mixed).length;
    },
};

export { getters };

